// 🔧 art_show_playbook.js
// ✅ Purpose: Public-facing event playbook for running an art show with Let's Go Vote
// ✅ Features:
// - Explains how art shows can use entries, categories, public voting, judging, QR codes, and awards
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Helps prospective customers understand the full art show workflow
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function ArtShowPlaybook() {
  return (
    <section id="top-content" className="container-fluid py-5">
    <SeoHead
      title="Art Show Playbook | Let's Go Vote"
      description="Learn how to run an art show with artist entries, categories, QR code voting, judged scoring, sponsors, and awards using Let's Go Vote."
      path="/guides/art-show-playbook"
      breadcrumbs={[
        { name: "Home", path: "/" },
        { name: "Guides", path: "/guides" },
        {
          name: "Art Show Playbook",
          path: "/guides/art-show-playbook",
        },
      ]}
    />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🎨 Art Show Playbook</h1>
          <p className="lead text-muted">
            Learn how to manage artist entries, categories, public voting,
            judging, sponsors, and awards.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">Designed for creative events</h5>
            <p className="mb-0">
              Let&apos;s Go Vote helps art show organizers collect entries,
              organize categories, support public voting or judged scoring, and
              display winners after the event.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 1: Create your art show event</h3>
          <p>
            Add your event name, dates, location details, description, featured
            images, and public event content. This gives artists and attendees a
            central place to learn about the show.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 2: Choose public voting or judging</h3>
          <p>
            Art shows may use People&apos;s Choice Voting for attendee favorites,
            Ranked Choice Voting for more detailed preferences, or judged scoring
            for formal evaluations.
          </p>

          <Link
            to="/guides/configure-voting"
            className="btn btn-sm btn-outline-primary"
          >
            Read the Voting Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 3: Create art categories</h3>
          <p>
            Categories might include Painting, Photography, Sculpture, Digital
            Art, Youth Art, Mixed Media, or Best in Show. Clear categories help
            voters and judges compare similar entries.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 4: Accept artist entries</h3>
          <p>
            Public entry submissions can allow artists to submit artwork details,
            photos, descriptions, and social links before the event. Managers can
            review and approve entries before they appear publicly.
          </p>

          <Link
            to="/guides/categories-and-entries"
            className="btn btn-sm btn-outline-primary"
          >
            Read Categories & Entries
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 5: Use QR codes beside artwork</h3>
          <p>
            QR codes can be placed near each artwork so attendees can scan, view
            details, and vote without needing a paper ballot.
          </p>

          <Link to="/guides/qr-codes" className="btn btn-sm btn-outline-primary">
            Read the QR Code Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 6: Publish winners and awards</h3>
          <p>
            After voting or judging closes, managers can review results and
            choose when winners are shown publicly. This can include category
            winners, Best in Show, or special sponsor awards.
          </p>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Planning an art show?</h5>
            <p className="mb-3">
              Let&apos;s Go Vote can help your event manage public entries, QR
              code voting, judged scoring, sponsors, and winner displays.
            </p>

            <Link to="/request_event" className="btn btn-primary">
              Request an Event
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default ArtShowPlaybook;